import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api";

const KPI = ({ label, value, testId, warn }) => (
  <div className={`border p-5 ${warn && value > 0 ? "border-burgundy/40 bg-burgundy/5" : "border-ink/10 bg-warmwhite"}`} data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50">{label}</p>
    <p className={`mt-2 font-serif text-3xl ${warn && value > 0 ? "text-burgundy" : "text-ink"}`}>{value}</p>
  </div>
);

export default function StudioDashboard() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => apiFetch("/admin/studio/dashboard").then((d) => {
    setData(d);
    setSettings(d.company);
  });
  useEffect(() => { load(); }, []);

  const saveSettings = () => {
    setSaving(true);
    apiFetch("/admin/studio/settings", { method: "PUT", body: JSON.stringify(settings) })
      .then((d) => setSettings(d))
      .finally(() => setSaving(false));
  };

  if (!data) return <p className="text-sm text-ink/50">Loading studio…</p>;
  const k = data.kpis;

  return (
    <div data-testid="studio-dashboard">
      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">IAGSB Digital Studio</p>
      <h1 className="mt-2 font-serif text-3xl text-ink">Studio Cockpit</h1>
      <p className="mt-2 max-w-xl text-sm text-ink/60">Payments are manually verified in this phase — mark an invoice paid only after you have confirmed the money arrived.</p>
      {data.test_data?.excluded_clients > 0 && (
        <p className="mt-2 max-w-xl text-xs text-ink/45" data-testid="test-data-excluded-note">
          {data.test_data.excluded_clients} TEST client{data.test_data.excluded_clients > 1 ? "s are" : " is"} flagged and fully excluded from every figure and list below.
        </p>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-3 xl:grid-cols-5">
        <KPI label="New enquiries" value={k.new_enquiries} testId="kpi-new-enquiries" />
        <KPI label="Pending quotations" value={k.pending_quotations} testId="kpi-pending-quotations" />
        <KPI label="Active projects" value={k.active_projects} testId="kpi-active-projects" />
        <KPI label="Pending deposits" value={k.pending_deposits} testId="kpi-pending-deposits" />
        <KPI label="Outstanding balances" value={`RM${Number(k.outstanding_balance).toLocaleString()}`} testId="kpi-outstanding" warn />
        <KPI label="MRR (paid retainers)" value={`RM${Number(k.mrr).toLocaleString()}`} testId="kpi-mrr" />
        <KPI label="Monthly care clients" value={k.active_monthly_clients} testId="kpi-monthly-clients" />
        <KPI label="Renewals due soon" value={k.upcoming_renewals} testId="kpi-renewals" warn />
        <KPI label="Overdue renewals" value={k.overdue_renewals} testId="kpi-overdue-renewals" warn />
        <KPI label="Open PAYG requests" value={k.open_payg_requests} testId="kpi-payg" />
        <KPI label="Appointments awaiting" value={k.appointments_awaiting} testId="kpi-appointments" warn />
        <KPI label="Overdue follow-ups" value={k.overdue_followups} testId="kpi-overdue" warn />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="border border-ink/10 bg-warmwhite p-6" data-testid="attention-appointments">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Appointments awaiting approval</p>
          {data.attention.appointments.length === 0 ? <p className="mt-3 text-sm text-ink/40">None pending.</p> : (
            <ul className="mt-3 space-y-2">
              {data.attention.appointments.map((a) => (
                <li key={a.id} className="text-sm text-ink">
                  <Link className="font-medium text-burgundy underline" to="/admin/studio/appointments">{(a.requested_at || "").replace("T", " ")}</Link> — {a.service}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-ink/10 bg-warmwhite p-6" data-testid="attention-followups">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Overdue follow-ups</p>
          {data.attention.followups.length === 0 ? <p className="mt-3 text-sm text-ink/40">None. Nice.</p> : (
            <ul className="mt-3 space-y-2">
              {data.attention.followups.map((c) => (
                <li key={c.id} className="text-sm">
                  <Link className="font-medium text-burgundy underline" to={`/admin/studio/clients/${c.id}`}>{c.business_name}</Link>
                  <span className="text-ink/50"> — {c.next_action} (due {c.next_action_date})</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-ink/10 bg-warmwhite p-6" data-testid="attention-invoices">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Unpaid invoices</p>
          {data.attention.unpaid_invoices.length === 0 ? <p className="mt-3 text-sm text-ink/40">None outstanding.</p> : (
            <ul className="mt-3 space-y-2">
              {data.attention.unpaid_invoices.map((d) => (
                <li key={d.id} className="text-sm">
                  <Link className="font-medium text-burgundy underline" to={`/admin/studio/clients/${d.client_id}`}>{d.number}</Link>
                  <span className="text-ink/50"> — RM{Number(d.total).toLocaleString()} {d.kind && `(${d.kind})`}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-ink/10 bg-warmwhite p-6" data-testid="attention-renewals">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Renewals needing attention</p>
          {(data.attention.renewals || []).length === 0 ? <p className="mt-3 text-sm text-ink/40">All subscriptions healthy.</p> : (
            <ul className="mt-3 space-y-2">
              {data.attention.renewals.map((s) => (
                <li key={s.id} className="text-sm">
                  <Link className="font-medium text-burgundy underline" to={`/admin/studio/clients/${s.client_id}`}>{s.label}</Link>
                  <span className={s.status === "Past Due" ? "font-semibold text-burgundy" : "text-amber"}> · {s.status} · due {s.next_renewal_date}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {settings && (
        <div className="mt-8 border border-ink/10 bg-warmwhite p-6" data-testid="studio-company-settings">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">Company details (appear on all documents)</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {[["legal_name", "Legal name"], ["reg_no", "Registration no."], ["tax_no", "Tax no."], ["sst", "SST status"], ["phone", "Phone"], ["email", "Email"]].map(([key, label]) => (
              <label key={key} className="text-xs text-ink/60">
                <span className="mb-1 block font-semibold uppercase tracking-[0.1em]">{label}</span>
                <input className="w-full border border-ink/15 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-amber" value={settings[key] || ""} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} data-testid={`studio-setting-${key}`} />
              </label>
            ))}
            <label className="text-xs text-ink/60 sm:col-span-2">
              <span className="mb-1 block font-semibold uppercase tracking-[0.1em]">Registered address</span>
              <input className="w-full border border-ink/15 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-amber" value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} data-testid="studio-setting-address" />
            </label>
          </div>
          <label className="mt-4 flex items-start gap-3 text-sm text-ink/75" data-testid="legal-review-wrap">
            <input type="checkbox" className="mt-1 h-4 w-4 accent-[#F47735]" checked={!!settings.agreement_legal_reviewed} onChange={(e) => setSettings({ ...settings, agreement_legal_reviewed: e.target.checked })} data-testid="legal-reviewed-toggle" />
            <span>Agreement template v{settings.agreement_version} has completed independent legal review. <span className="text-ink/50">(Agreements cannot be sent to clients until this is ticked.)</span></span>
          </label>
          <div className="mt-5 grid gap-4 border-t border-ink/10 pt-5 sm:grid-cols-2" data-testid="studio-reminder-settings">
            <label className="text-xs text-ink/60">
              <span className="mb-1 block font-semibold uppercase tracking-[0.1em]">Renewal reminder lead time (days)</span>
              <input type="number" min="1" max="30" className="w-full border border-ink/15 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-amber" value={settings.reminder_days ?? 7} onChange={(e) => setSettings({ ...settings, reminder_days: parseInt(e.target.value) || 7 })} data-testid="studio-setting-reminder-days" />
            </label>
            <label className="flex items-start gap-3 pt-5 text-sm text-ink/75">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[#F47735]" checked={!!settings.reminders_paused} onChange={(e) => setSettings({ ...settings, reminders_paused: e.target.checked })} data-testid="studio-setting-reminders-paused" />
              <span>Pause ALL renewal reminder emails (invoices still exist; nothing is auto-charged either way).</span>
            </label>
          </div>
          <button onClick={saveSettings} disabled={saving} className="mt-4 border border-ink/20 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-ink hover:border-amber disabled:opacity-50" data-testid="studio-settings-save">
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}
    </div>
  );
}
